import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp, Bug, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { usePortalPWA } from '@/hooks/usePortalPWA';

export function PWADebugPanel() {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const pwaState = usePortalPWA();
  
  // Only show debug panel when pwadebug URL parameter is present
  const showDebug = searchParams.get('pwadebug') === '1';
  if (!showDebug) {
    return null;
  }
  
  const {
    isInstallable,
    isInstalled,
    canInstall,
    isIOS,
    isMobile,
    isPWACapable,
    hasServiceWorker,
    hasManifest,
    isSecure,
    debugInfo
  } = pwaState;

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    );
  };

  const getStatusBadge = (status: boolean, trueText: string, falseText: string) => {
    return (
      <Badge variant={status ? "default" : "destructive"} className="text-xs">
        {status ? trueText : falseText}
      </Badge>
    );
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="bg-background/95 backdrop-blur-sm border-2 shadow-lg"
          >
            <Bug className="h-4 w-4 mr-2" />
            PWA Debug
            {isOpen ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-2">
          <Card className="bg-background/95 backdrop-blur-sm border-2 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bug className="h-4 w-4" />
                PWA Installation Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Main Status */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Can Install</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(canInstall)}
                    {getStatusBadge(canInstall, "Ready", "Not Ready")}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Installed</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(isInstalled)}
                    {getStatusBadge(isInstalled, "Installed", "Not Installed")}
                  </div>
                </div>
              </div>

              {/* Technical Requirements */}
              <div className="border-t pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Technical Requirements</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs">Secure Context</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(isSecure)}
                    {getStatusBadge(isSecure, "HTTPS", "HTTP")}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs">Service Worker</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(hasServiceWorker)}
                    {getStatusBadge(hasServiceWorker, "Available", "Missing")}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs">Manifest</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(hasManifest)}
                    {getStatusBadge(hasManifest, "Found", "Missing")}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs">Browser Prompt</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(isInstallable)}
                    {getStatusBadge(isInstallable, "Ready", "Waiting")}
                  </div>
                </div>
              </div>

              {/* Device Info */}
              <div className="border-t pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Device & Browser</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs">PWA Capable</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(isPWACapable)}
                    {getStatusBadge(isPWACapable, "Supported", "Not Supported")}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs">iOS Device</span>
                  {getStatusBadge(isIOS, "iOS", "Other")}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs">Mobile Device</span>
                  {getStatusBadge(isMobile, "Mobile", "Desktop")}
                </div>
              </div>

              {/* Debug Info */}
              {debugInfo && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Debug Info</p>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>User Agent: {navigator.userAgent.substring(0, 50)}...</div>
                    <div>Current Path: {window.location.pathname}</div>
                    <div>Display Mode: {window.matchMedia('(display-mode: standalone)').matches ? 'Standalone' : 'Browser'}</div>
                    <div>Viewport: {window.innerWidth}x{window.innerHeight}</div>
                    <div>Screen: {window.screen.width}x{window.screen.height}</div>
                  </div>
                </div>
              )}

              {/* Active Manifest Details */}
              <div className="border-t pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Manifest Status</p>
                <div className="text-xs text-muted-foreground space-y-1">
                  {(() => {
                    const manifestLink = document.getElementById('manifest-link') as HTMLLinkElement;
                    const portalLink = document.getElementById('portal-manifest-link') as HTMLLinkElement;
                    const staffLink = document.getElementById('staff-manifest-link') as HTMLLinkElement;
                    
                    return (
                      <>
                        <div>Main Link: {manifestLink?.href || 'Not found'}</div>
                        <div>Main Disabled: {manifestLink?.disabled ? 'Yes' : 'No'}</div>
                        <div>Portal Link: {portalLink?.href || 'Not found'}</div>
                        <div>Portal Disabled: {portalLink?.disabled ? 'Yes' : 'No'}</div>
                        <div>Staff Link: {staffLink?.href || 'Not found'}</div>
                        <div>Staff Disabled: {staffLink?.disabled ? 'Yes' : 'No'}</div>
                        <div>Active: {document.querySelector('link[rel="manifest"]:not([disabled])')?.getAttribute('href') || 'None'}</div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t pt-3 space-y-2">
                <Button 
                  size="sm" 
                  className="w-full text-xs" 
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="w-full text-xs"
                  onClick={() => {
                    navigator.serviceWorker.getRegistrations().then(registrations => {
                      console.log('Service Worker Registrations:', registrations);
                    });
                    
                    // Check manifests
                    const manifests = document.querySelectorAll('link[rel="manifest"]');
                    console.log('Manifest Links:', Array.from(manifests).map(link => ({
                      href: link.getAttribute('href'),
                      disabled: (link as HTMLLinkElement).disabled,
                      media: link.getAttribute('media')
                    })));
                    
                    // Test manifest accessibility
                    const activeManifest = document.querySelector('link[rel="manifest"]:not([disabled])');
                    if (activeManifest) {
                      fetch(activeManifest.getAttribute('href') || '')
                        .then(res => res.json())
                        .then(data => console.log('Active manifest content:', data))
                        .catch(err => console.error('Manifest fetch error:', err));
                    }
                  }}
                >
                  Check Service Workers & Manifests
                </Button>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}